import { checkHistoryActions } from 'constants/check-history-actions';
import { CheckHistoryTypes } from 'constants/check-history-types';
import { saveAs } from 'file-saver';
import moment from 'moment';

const extractTextFromReactElement = element => {
  if (!element) return '';

  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }

  if (typeof element === 'boolean') {
    return '';
  }

  if (Array.isArray(element)) {
    return element.map(extractTextFromReactElement).filter(Boolean).join('');
  }

  if (typeof element === 'object') {
    if (element?.props) {
      const fromChildren =
        element.props.children !== undefined
          ? extractTextFromReactElement(element.props.children)
          : '';
      if (fromChildren) return fromChildren;

      // Components that render text via props instead of children (e.g. UserInfo, OverlayCard)
      if (typeof element.props.display === 'string') return element.props.display;
      if (element.props.firstName != null || element.props.lastName != null) {
        return [element.props.firstName, element.props.lastName].filter(Boolean).join(' ').trim();
      }
      if (typeof element.props.name === 'string') return element.props.name;
    }
  }

  return '';
};

const DESCRIPTION_UNAVAILABLE = 'Description unavailable';

export const getHistoryActionText = (entry, actionData) => {
  const action = checkHistoryActions[entry.type];

  if (!action) {
    return { ok: true, text: 'Unknown action' };
  }

  try {
    const actionResult = action(actionData);

    if (!actionResult?.description) {
      return { ok: true, text: '' };
    }

    return {
      ok: true,
      text: extractTextFromReactElement(actionResult.description),
    };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[history-export] Failed to build description for entry', entry?.type, err);
    }
    return { ok: false, text: DESCRIPTION_UNAVAILABLE };
  }
};

export const getEventTypeName = type => {
  const typeKey = Object.keys(CheckHistoryTypes).find(key => CheckHistoryTypes[key] === type);

  if (!typeKey) return 'Unknown';

  return typeKey
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
};

/** Plain-text fields shown on a history row — used by History search. */
export const getHistoryEntrySearchText = (
  entry,
  candidate,
  { referees, companyUsers, companyChecks, company } = {},
) => {
  const actionData = buildActionDataForEntry(
    entry,
    candidate,
    referees,
    companyChecks,
    companyUsers,
    company,
  );
  const { text: description } = getHistoryActionText(entry, actionData);
  return [
    description,
    getEventTypeName(entry.type),
    entry.user_name,
    entry.message,
    actionData.checkHuman,
    actionData.refReferee,
    actionData.country,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

// Mitigate CSV injection when files are opened in Excel / Sheets / LibreOffice: cells that
// begin with =, +, -, @, tab, or CR can be interpreted as formulas; leading whitespace before
// =,+,-,@ is also risky. Prefix with ' (spreadsheet text marker).
// See OWASP: https://owasp.org/www-community/attacks/CSV_Injection
const csvCellNeedsInjectionGuard = s => {
  if (s.startsWith('\t') || s.startsWith('\r')) return true;
  const trimmed = s.replace(/^\uFEFF/, '').trimStart();
  const c = trimmed[0];
  return c === '=' || c === '+' || c === '-' || c === '@';
};

const escapeCSV = str => {
  if (str == null) return '';

  let stringValue = String(str);

  if (csvCellNeedsInjectionGuard(stringValue)) {
    stringValue = `'${stringValue}`;
  }

  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

// Strip characters unsafe or disallowed in download filenames across OSes and browsers.
const FILENAME_SEGMENT_MAX_LEN = 120;

const sanitizeFilenameSegment = (raw, fallback = 'candidate') => {
  if (raw == null || raw === '') return fallback;

  let s = String(raw)
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, FILENAME_SEGMENT_MAX_LEN);

  s = s.replace(/^\.+/, '');
  return s || fallback;
};

const buildActionDataForEntry = (
  entry,
  candidate,
  referees,
  companyChecks,
  companyUsers,
  company,
) => {
  const checkHuman = entry?.check_type
    ? companyChecks?.find(companyCheck => companyCheck.type === entry.check_type)?.check_type.title
    : null;

  const userName = entry.user_name || 'User';
  const refCandidate = candidate.name;

  const country =
    entry.country && entry.country !== 'Not specified.' ? `from ${entry.country}` : '';

  const referee = entry.referee_id ? referees?.find(r => r.id === entry.referee_id) : null;
  const refReferee = referee ? referee.name : null;

  const candidateRefereeLinks =
    referees && referees.length > 0
      ? referees.map((ref, index) => `${index > 0 ? ' & ' : ''}${ref.name}`).join('')
      : null;

  return {
    refCandidate,
    refReferee,
    referenceLink: null,
    candidateRefereeLinks,
    backgroundChecks: null,
    country,
    styles: {},
    userName,
    candidate,
    checkHuman,
    entry,
    referee,
    onCandidateRequest: () => {},
    onRefereeRequest: () => {},
    referees,
    companyUsers,
  };
};

export const exportHistoryToCSV = (
  entries,
  candidate,
  { referees, companyUsers, companyChecks, company },
) => {
  if (!entries || entries.length === 0) {
    return { descriptionFailureCount: 0 };
  }

  const headers = ['Date', 'Time', 'Event Type', 'Actor', 'Description'];

  let descriptionFailureCount = 0;

  const rows = entries.map(entry => {
    const actionData = buildActionDataForEntry(
      entry,
      candidate,
      referees,
      companyChecks,
      companyUsers,
      company,
    );

    const { ok, text: description } = getHistoryActionText(entry, actionData);
    if (!ok) {
      descriptionFailureCount += 1;
    }

    const eventType = getEventTypeName(entry.type);
    const date = moment(entry.created_at).format('MM/DD/YYYY');
    const time = moment(entry.created_at).format('HH:mm:ss');
    const actor = entry.user_name || 'User';

    return [date, time, eventType, actor, description];
  });

  const csvLines = [headers, ...rows];
  const csvContent = csvLines.map(row => row.map(escapeCSV).join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const safeCandidateName = sanitizeFilenameSegment(candidate?.name);
  const fileName = `${safeCandidateName}_history_${moment().format('YYYY_MM_DD')}.csv`;

  saveAs(blob, fileName);

  return { descriptionFailureCount };
};
